import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { log } from 'log';
import { prop } from 'ramda';
import uuid from 'uuid-random';
import { useForm } from 'react-final-form';
import { useTranslations } from 'js/hooks/i18n';
import { useAppSelector } from 'js/store/hooks';
import { Icon } from 'js/components/common/Icon';
import { changeInputValue } from 'js/utils/DOM.util';
import { clsb, clsx } from 'js/utils/classNames.util';
import { componentService } from 'js/services/component.service';

import * as Types from './types';
import { Label } from '../Label';
import * as helpers from '../../helpers';
import { Suggestions } from './components';
import { parseSuggestions } from './helpers';
import { withFinalFormField } from '../../hocs';
import { ClearInputIcon } from '../ClearInputIcon';
import { FieldBottomMessage } from '../FieldBottomMessage';

import '../../FormFields.scss';
import style from './Input.scss';

export const InputBare: React.FC<Types.IInputProps> = ({
    className,
    classNames,
    currencySymbol,
    disabled,
    escapeSymbolsRegExp,
    hint,
    id,
    infoTooltip,
    isLabelAsteriskEnabled,
    isPlaceholderAsteriskEnabled,
    label,
    leftIcon,
    meta,
    name = '',
    onBlur,
    onChange,
    onFocus,
    onInput,
    onMouseDown,
    placeholder = '',
    readOnly,
    rightIcon,
    showPasswordEye,
    suggestions,
    type,
    withClearInputIcon = false,
    inputRef: customInputRef,

    ...props
}) => {
    const idRef = useRef<string>(id || uuid());
    const defaultInputRef = useRef<HTMLInputElement>(null);
    const suggestionsRef = useRef<HTMLSelectElement>(null);
    const [isPasswordVisible, setIsPasswordVisible] = useState(false);
    const translate = useTranslations('playtech.mobile.system.input.');
    const isEnablePasswordSpy = useAppSelector(prop('isEnablePasswordSpy'));
    const isDisabled = meta?.submitting || meta?.validating || disabled;
    const showPasswordIcon = (showPasswordEye || isEnablePasswordSpy) && type === 'password';
    const inputRef = customInputRef || defaultInputRef;

    const [showClearInputIcon, setShowClearInputIcon] = useState<boolean>(Boolean(inputRef.current?.value));

    const interactionClass = helpers.getInteractionClass({
        readOnly,
        error: meta?.error,
        disabled: isDisabled,
        active: meta?.active,
        touched: meta?.touched,
        submitting: meta?.submitting
    });

    const togglePasswordVisible = useCallback(() => {
        setIsPasswordVisible(!isPasswordVisible);
    }, [setIsPasswordVisible, isPasswordVisible]);
    const ariaLabel = props['aria-label']
        ? `${currencySymbol || ''} ${props['aria-label']}`
        : translate('playtech.mobile.system.input.aria-label');

    const parsedSuggestions = useMemo(() => parseSuggestions(suggestions), [suggestions]);
    const hasSuggestions = Boolean(parsedSuggestions?.length) && !isDisabled && !readOnly;
    const onCombinedChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
        onChange?.(event);
        setShowClearInputIcon(Boolean(event.target?.value));

        if (suggestionsRef.current) {
            suggestionsRef.current.value = event.target.value;
        }
    }, [onChange, setShowClearInputIcon]);

    const onCombinedInput = useCallback((event: React.ChangeEvent<HTMLInputElement>): void => {
        if (escapeSymbolsRegExp) {
            try {
                event.target.value = event.target.value.replace(new RegExp(escapeSymbolsRegExp), '');
            } catch (err) {
                log('error', err);
            }
        }

        onInput?.(event);
    }, [escapeSymbolsRegExp, onInput]);

    const onSuggestionsChange = useCallback((event: React.ChangeEvent<HTMLSelectElement>) => {
        changeInputValue({ value: event.target.value, input: inputRef.current });
    }, [inputRef]);

    const onClearInputIconClick = useCallback((): void => {
        changeInputValue({ value: '', input: inputRef.current });
        inputRef.current?.focus();
    }, [inputRef]);

    return (
        <div
            onFocus={onFocus}
            onBlur={onBlur}
            className={clsx([clsb([style, classNames], ['container']), interactionClass, className])}
        >
            {(label || isLabelAsteriskEnabled) && (
                <Label
                    label={label}
                    id={idRef.current}
                    readOnly={readOnly}
                    classNames={classNames}
                    infoTooltip={infoTooltip}
                    isLabelAsteriskEnabled={isLabelAsteriskEnabled}
                />
            )}
            <div className={clsb([style, classNames], hasSuggestions ? ['innerContainer', 'withSuggestions'] : ['innerContainer'])}>
                <div className={clsb([style, classNames], ['innerInputContainer'])}>
                    {leftIcon && leftIcon()}

                    {currencySymbol && <span className={clsb([style, classNames], ['currencySymbol'])}>{currencySymbol}</span>}

                    <input
                        {...props}
                        name={name}
                        ref={inputRef}
                        id={idRef.current}
                        readOnly={readOnly}
                        disabled={isDisabled}
                        aria-label={ariaLabel}
                        onChange={onCombinedChange}
                        tabIndex={isDisabled || readOnly ? -1 : 0}
                        type={isPasswordVisible ? 'text' : type}
                        className={clsb([style, classNames], ['input'])}
                        onMouseDown={readOnly ? helpers.preventDefault : onMouseDown}
                        placeholder={`${placeholder}${isPlaceholderAsteriskEnabled ? '*' : ''}`}
                        {...escapeSymbolsRegExp ? { onInput: onCombinedInput } : { onInput }}
                    />

                    {withClearInputIcon && showClearInputIcon && !isDisabled && !readOnly && (
                        <ClearInputIcon
                            onClick={onClearInputIconClick}
                            className={classNames?.clearInputIcon}
                        />
                    )}

                    {showPasswordIcon &&
                        (isPasswordVisible ? (
                            <Icon
                                type="passwordEyeShow"
                                onClick={togglePasswordVisible}
                                disabled={isDisabled || readOnly}
                                className={clsb([style, classNames], ['showPasswordIcon'])}
                                aria-label={translate('password.aria-label.hide')}
                            />
                        ) : (
                            <Icon
                                type="passwordEyeHide"
                                onClick={togglePasswordVisible}
                                disabled={isDisabled || readOnly}
                                className={clsb([style, classNames], ['showPasswordIcon'])}
                                aria-label={translate('password.aria-label.show')}
                            />
                        ))}

                    {rightIcon && rightIcon()}
                </div>
                {parsedSuggestions && hasSuggestions && (
                    <Suggestions
                        className={clsb([style, classNames], ['suggestions'])}
                        id={`${idRef.current}_suggestions`}
                        onChange={onSuggestionsChange}
                        suggestions={parsedSuggestions}
                        suggestionsRef={suggestionsRef}
                    />
                ) || null}
            </div>

            <FieldBottomMessage hint={hint} meta={meta} classNames={classNames} />
        </div>
    );
};

export const InputFinalForm: React.FC<Types.IInputProps> = ({
    inputRef: customInputRef,
    meta = {},
    name,
    ...props
}) => {
    const formAPI = useForm();
    const focusRef = useRef(false);
    const defaultInputRef = useRef<HTMLInputElement>(null);
    const inputRef = customInputRef || defaultInputRef;
    const { active = false, touched = false } = meta;

    useEffect(() => {
        if (!name) {
            return;
        }

        const onFocusInHandler = (event: FocusEvent): void => {
            if (event.target === inputRef.current) {
                focusRef.current = true;
            }
        };

        const onBlurHandler = (event: FocusEvent): void => {
            if (event.target !== inputRef.current || active) {
                return;
            }

            if (focusRef.current) {
                focusRef.current = false;

                return;
            }

            // A native blur (not focusout) event without previous focusin event occurs on browser autofill.
            // We need to trigger RFF validation via API blur or resetFieldState methods.
            // blur - will highlight field according to validation results
            // resetFieldState - will provide silent validation.
            if (touched) {
                formAPI.blur(name);
            } else {
                formAPI.resetFieldState(name);
            }
        };

        document.body.addEventListener('focusin', onFocusInHandler);
        document.body.addEventListener('blur', onBlurHandler, true);

        return () => {
            document.body.removeEventListener('focusin', onFocusInHandler);
            document.body.removeEventListener('blur', onBlurHandler, true);
        };
    }, [active, formAPI, inputRef, name, touched]);

    return (
        <InputBare
            inputRef={inputRef}
            meta={meta}
            name={name}
            {...props}
        />
    );
};

export const InputPure = memo(InputBare);
export const Input = withFinalFormField<string>()(InputFinalForm);

componentService.registerWebContentComponents({ Input: InputPure });
